import api from '../axios_Interceptor/api';
import api2 from '../axios_Interceptor/api2';
import { apiBaseUrl } from '../connection';
import { io, Socket } from 'socket.io-client';
import { SOCKET_SERVER_URL } from '../server';

const listeners = new Set<() => void>();
let socket: WebSocket | null = null;
let fallbackSocket: Socket | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let generation = 0;
let connecting = false;

const notify = () => listeners.forEach(listener => listener());

function connectFallback(current: number) {
  if (fallbackSocket || current !== generation || !listeners.size) return;
  const connection = io(SOCKET_SERVER_URL, { transports: ['websocket'] });
  fallbackSocket = connection;
  connection.on('connect', notify);
  connection.on('Support Changed', notify);
  connection.on('support:changed', notify);
}

async function connect() {
  if (!listeners.size || connecting || socket) return;
  // The API selects its transport at runtime; production builds may omit this flag.
  if (process.env.REACT_APP_REALTIME_ENABLED === 'false') {
    connectFallback(generation);
    return;
  }
  connecting = true;
  const current = generation;
  try {
    const platform = window.location.pathname.startsWith('/admin_main');
    const response = platform
      ? await api2.post(`${apiBaseUrl()}realtime/platform-token`, {}, { headers: { 'X-Fanbird-Admin': '1' }, timeout: 10000 })
      : await api.post(`${apiBaseUrl()}realtime/support-token`, {}, { timeout: 10000 });
    if (current !== generation || !listeners.size) return;
    const connection = new WebSocket(response.data.endpoint, response.data.token);
    socket = connection;
    connection.onopen = () => {
      fallbackSocket?.disconnect(); fallbackSocket = null;
      notify();
    };
    connection.onmessage = event => {
      try { if (JSON.parse(event.data).type === 'support:changed') notify(); } catch { /* Ignore malformed transport frames. */ }
    };
    connection.onclose = () => {
      if (socket === connection) socket = null;
      if (current === generation && listeners.size) timer = setTimeout(connect, 2000);
    };
  } catch {
    // The existing Socket.IO gateway keeps local and staged deployments live;
    // HTTP polling remains the last recovery path during transport outages.
    connectFallback(current);
    if (current === generation && listeners.size) timer = setTimeout(connect, 10000);
  } finally { if (current === generation) connecting = false; }
}

export function subscribeSupportActivity(listener: () => void) {
  listeners.add(listener);
  void connect();
  return () => {
    listeners.delete(listener);
    if (listeners.size) return;
    generation++;
    if (timer) clearTimeout(timer);
    timer = null; connecting = false;
    socket?.close(); socket = null;
    fallbackSocket?.disconnect(); fallbackSocket = null;
  };
}
